import { Comment } from "@iventis/domain-model/model/comment";
import { User } from "@iventis/domain-model/model/user";
import { createContext, useContext } from "react";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { ResourceType } from "@iventis/domain-model/model/resourceType";
import { Asset } from "@iventis/domain-model/model/asset";
import { CommentImage } from "./comment-types";

export type CreateComment = Pick<Comment, "id" | "textContent" | "userId" | "resourceType" | "resourceId">;

export interface ICommentService {
    /**
     * The resource that the comments are associated with.
     */
    resource: { id: string; type: ResourceType };
    /**
     * Gets a comment by it's id.
     */
    get: (id: string, resourceId: string) => Promise<Comment>;

    /**
     * Gets an array of comments with a matching resource id.
     *
     * Resource examples: Map, Layer, Map Object etc.
     */
    getByResourceId: (resourceId: string) => Promise<Comment[]>;

    /**
     * Deletes the comment of a given id.
     */
    delete: (id: string, resourceId: string) => Promise<void>;

    /**
     * Updates a comment. This only supports textContent updates.
     */
    update: (comment: Comment, resourceId: string) => Promise<void>;

    /**
     * Creates a new comment.
     */
    create: (comment: CreateComment, resourceId: string) => Promise<void>;

    /**
     * Given the id of a user, will return a user object.
     */
    getUser: (userId: string) => Promise<User>;

    /**
     * Currently logged in user.
     */
    loggedInUser: { id: string; resourcePermission: Authorisation };

    /**
     * Given a user id, will return a URL to the user's profile picture asset.
     */
    getUserProfilePicture: (userId: string) => Promise<string>;

    /**
     * Cancel the comment creation or update.
     */
    cancel: () => void;

    /**
     * Optional callback to focus on the comment if required outside of the comment context.
     */
    focusOnComment?: (commentId: string) => void;

    /**
     * Given a comment image id, will return a URL to the comment image asset
     */
    getCommentImage: (commentImageId: string) => Promise<{ url: string; name: string }>;

    /**
     * Given an assetId and Base64 of a image, will post to asset service
     */
    postCommentImage: (assetId: string, commentImage: CommentImage) => Promise<Asset>;
}

export const CommentsServiceContext = createContext<ICommentService>({
    resource: null,
    get: null,
    getByResourceId: null,
    delete: null,
    update: null,
    create: null,
    getUser: null,
    getUserProfilePicture: null,
    cancel: null,
    loggedInUser: null,
    getCommentImage: null,
    postCommentImage: null,
});

export const useCommentsService = () => useContext(CommentsServiceContext);
